import { Alert, Box, Grid, Typography } from "@mui/material";
import EventSelectManyInput from "atoms/Inputs/EventSelectManyInput";
import { formatBytes } from "misc/formatHelpers";
import MDXEditorInput from "misc/mdx/MDXEditorInput";
import DialogContextFormDirtyChecker from "molecules/DialogContextFormDirtyChecker";
import { ContentBlockRecordType, EventBlockRecordType } from "pages/ExternalEmailTemplate/ExternalEmailTemplate.types";
import { FileRecordType } from "pages/SharedRecords.types";
import { Fragment, useEffect, useState } from "react";
import { CheckboxGroupInput, useTranslate } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";

import EmailReceiverList from "./EmailReceiverList";
import EmailTemplateAndSubjectInput from "./EmailTemplateAndSubjectInput";
import { ComposeEmailFormContentPropsType } from "./SendEmailDialog.types";

export default function ComposeEmailFormContent(props: ComposeEmailFormContentPropsType) {
  const { recipients, templates, defaultTemplate, availableFiles, showPreview, previewContent, withGeneratedDocument } =
    props;
  const translate = useTranslate();
  const template = useWatch<{ template: number }>({ name: "template" });
  const watchedAttachments = useWatch<{ attachments: string[] }>({ name: "attachments" });
  const { setValue } = useFormContext();
  const [exceedsSizeLimit, setExceedsSizeLimit] = useState(false);
  const [contentBlocks, setContentBlocks] = useState<ContentBlockRecordType[]>([]);
  const [eventBlocks, setEventBlocks] = useState<EventBlockRecordType[]>([]);

  useEffect(() => {
    const selectedTemplate = templates.find((t) => t.id === template);

    if (selectedTemplate) {
      if (selectedTemplate.sender) {
        setValue("sender", selectedTemplate.sender, { shouldDirty: true });
      }
      setValue("subject", selectedTemplate.subject, { shouldDirty: true });
      setContentBlocks(selectedTemplate.content_blocks);
      setEventBlocks(selectedTemplate.event_blocks);

      selectedTemplate.content_blocks.forEach((block) => {
        setValue(`content_blocks[${block.slug}]`, block.content);
      });
    }
  }, [template]);

  useEffect(() => {
    if (!watchedAttachments || !availableFiles) return;

    const selectedFiles = availableFiles.filter((f) => watchedAttachments.includes(f.id.toString()));
    const totalSize = selectedFiles.reduce((acc, f) => acc + (f.file_size || 0), 0);

    setExceedsSizeLimit(totalSize > 18000000); // 20MB (Brevo limit)
  }, [watchedAttachments, availableFiles]);

  useEffect(() => {
    if (defaultTemplate) {
      setContentBlocks(defaultTemplate.content_blocks);
    }
  }, [templates]);

  const selectedTemplate = templates.find((t) => t.id === template);

  return (
    <Fragment>
      <DialogContextFormDirtyChecker />
      <Box display={showPreview ? "none" : "block"} width="100%">
        <EmailReceiverList
          recipients={recipients}
          useInvoiceContactTypeInitially={selectedTemplate?.scope == "INVOICE_TEMPLATES"}
        />

        <EmailTemplateAndSubjectInput templates={templates} defaultTemplate={defaultTemplate} />

        {contentBlocks.map((block) => (
          <Box key={block.id} mb={3}>
            <Typography sx={{ mb: 0.5 }} variant="caption" color="textPrimary">
              {block.label}
            </Typography>
            <Box sx={{ flexDirection: "column", flex: 1, display: "flex" }}>
              <MDXEditorInput source={`content_blocks[${block.slug}]`} defaultValue={block.content} />
            </Box>
          </Box>
        ))}

        {eventBlocks.map((eventBlock) => (
          <EventSelectManyInput
            key={eventBlock.slug}
            source={`event_blocks[${eventBlock.slug}]`}
            reference="events"
            label={eventBlock.label}
          />
        ))}

        {withGeneratedDocument && (
          <Alert severity="info" sx={{ width: "94%", marginBottom: 1 }}>
            {translate("emailing.with_generated_document")}
          </Alert>
        )}

        {exceedsSizeLimit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("emailing.total_file_size_exceeded_warning")}
          </Alert>
        )}

        {availableFiles.length > 0 && (
          <CheckboxGroupInput
            label="emailing.attachments"
            source="attachments"
            choices={availableFiles}
            optionText={(choice: FileRecordType) =>
              `${choice.original_filename}${choice.file_size ? ` (${formatBytes(choice.file_size)})` : ""}`
            }
            row={false}
          />
        )}
      </Box>
      {showPreview && (
        <Grid container my={4}>
          <Grid item xs={12} minHeight={"60vh"}>
            <iframe title="Preview" srcDoc={previewContent} style={{ width: "100%", height: "100%", border: "none" }} />
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
}
